import React, { useState, useEffect } from "react";
import { BrowserRouter as Router, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./components/Login";
import ProducerRegister from "./components/ProducerRegister";
import DataTable from "./components/DataTable";
import CollectionSelector from "./components/CollectionSelector";
import MyBikes from "./components/MyBikes";
import AddBikePage from "./components/AddBikePage";
import { getCachedCollection } from "./services/firestoreCache";

// Style dla całej aplikacji
const appStyles = {
  backgroundColor: "#0a192f",
  color: "#e6f1ff",
  minHeight: "100vh",
  fontFamily: "'Segoe UI', 'Roboto', sans-serif"
};

const headerStyles = {
  backgroundColor: "#112240",
  padding: "16px",
  borderRadius: "8px",
  boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
  marginBottom: "0px"
};

const logoutButtonStyles = {
  padding: "10px 16px",
  backgroundColor: "#f44336",
  color: "white",
  border: "none",
  borderRadius: "4px",
  cursor: "pointer",
  boxShadow: "0 2px 4px rgba(0, 0, 0, 0.2)"
};

const getFieldValue = (record: Record<string, any>, fieldName: string) => {
  const key = Object.keys(record).find(k => k.toLowerCase() === fieldName.toLowerCase());
  return key ? record[key] : undefined;
};

const getTimestampValueMs = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;

  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? 0 : ms;
  }

  if (typeof value === "object") {
    if ("seconds" in value && typeof (value as any).seconds === "number") {
      return (value as any).seconds * 1000;
    }
    if ("toDate" in value && typeof (value as any).toDate === "function") {
      const date = (value as any).toDate();
      return Number.isNaN(date?.getTime?.()) ? 0 : date.getTime();
    }

    const dateLikeKeys = ["date", "value", "timestamp", "$date", "created_at", "createdat", "created_on", "createdon", "datetime", "time"];
    for (const key of dateLikeKeys) {
      const nestedValue = (value as Record<string, any>)[key];
      if (nestedValue !== undefined) {
        const nestedMs = getTimestampValueMs(nestedValue);
        if (nestedMs > 0) return nestedMs;
      }
    }

    for (const [key, nestedValue] of Object.entries(value as Record<string, any>)) {
      const lowerKey = key.toLowerCase();
      if (/(created|added|date|timestamp|time)/.test(lowerKey)) {
        const nestedMs = getTimestampValueMs(nestedValue);
        if (nestedMs > 0) return nestedMs;
      }
    }
  }

  if (typeof value === "string") {
    const normalized = value.trim();
    if (!normalized) return 0;

    if (/^\d{10}(\.\d+)?$/.test(normalized)) {
      const numeric = Number(normalized);
      return Number.isFinite(numeric) ? (numeric > 1e12 ? numeric : numeric * 1000) : 0;
    }

    if (/^\d{13,}$/.test(normalized)) {
      const numeric = Number(normalized);
      return Number.isFinite(numeric) ? numeric : 0;
    }

    const candidateStrings = [
      normalized,
      normalized.replace(/\s+/, "T"),
      normalized.replace(" ", "T"),
      normalized.replace(/\s+/g, "T"),
      normalized.replace(/\.?\d{3}Z$/i, "Z"),
      normalized.replace(/\s+UTC$/i, "Z")
    ];

    for (const candidate of candidateStrings) {
      const parsed = Date.parse(candidate);
      if (!Number.isNaN(parsed)) return parsed;
    }
    return 0;
  }

  if (typeof value === "number") {
    if (!Number.isFinite(value)) return 0;
    return value > 1e12 ? value : value * 1000;
  }

  return 0;
};

const getLatestRecordTimestamp = (doc: Record<string, any>, collectionName: string): number => {
  const candidates = [
    "updatedAt",
    "createdAt",
    "addedAt",
    "date",
    "timestamp",
    "created_at",
    "updated_at",
    "added_at",
    "created",
    "updated",
    "registeredAt",
    "registered_at",
    "lastUpdated",
    "last_updated"
  ];

  const lowerCollection = collectionName.toLowerCase();
  const fieldNames = lowerCollection === "users"
    ? [...candidates, "birthDate", "birth_date", "createdOn", "created_on"]
    : [...candidates, "year", "rok", "prodYear", "productionYear"];

  for (const fieldName of fieldNames) {
    const value = getFieldValue(doc, fieldName);
    const ms = getTimestampValueMs(value);
    if (ms > 0) return ms;
  }

  return 0;
};

// Komponent chroniący ścieżki z opcjonalną rolą
const ProtectedRoute: React.FC<{ children: React.ReactNode; role?: string }> = ({ children, role }) => {
  const { currentUser, loading, role: userRole } = useAuth();

  if (loading) return <div style={appStyles}>Ładowanie...</div>;
  if (!currentUser) {
    return <Navigate to="/login" />;
  }
  if (role && userRole !== role) {
    // Jeśli wymagana rola, a użytkownik jej nie ma
    return <Navigate to={userRole === "producer" ? "/my-bikes" : "/login"} />;
  }
  return <>{children}</>;
};

const MainPage: React.FC = () => {
  const [collectionName, setCollectionName] = useState<string>("bikeFrames");
  const { logout, role } = useAuth();
  const [collectionStats, setCollectionStats] = useState({ total: 0, producers: 0, countries: 0 });
  const [stats, setStats] = useState<{
    total: number;
    lastRecords: Array<{
      date: string;
      rok?: string;
      producent?: string;
      model?: string;
      rozmiar?: string;
      country?: string;
      birthYear?: string;
      gender?: string;
      height?: string;
    }>;
  }>({
    total: 0,
    lastRecords: []
  });

  useEffect(() => {
    getCachedCollection("bikeFrames", { maxAgeMs: 5 * 60 * 1000, forceRefresh: false }).catch(() => {});
  }, []);

  useEffect(() => {
    const navigationEntries = typeof performance !== "undefined"
      ? (performance.getEntriesByType("navigation") as PerformanceNavigationTiming[])
      : [];
    const isReload = navigationEntries.length > 0 && navigationEntries[0].type === "reload";
    if (!isReload) return;

    setCollectionName("bikeFrames");
    try {
      Object.keys(localStorage).forEach((key) => {
        if (key.startsWith("columnOrder_") || key.startsWith("hiddenColumns_")) {
          localStorage.removeItem(key);
        }
      });
    } catch (error) {
      console.error("Błąd resetu ustawień widoku po odświeżeniu:", error);
    }
  }, []);

  const fetchStats = async (forceRefresh: boolean) => {
    try {
      const allDocs = await getCachedCollection(collectionName, {
        maxAgeMs: 5 * 60 * 1000,
        forceRefresh
      });

      const sortedDocs = [...allDocs].sort((a: any, b: any) => {
        const aTime = getLatestRecordTimestamp(a, collectionName);
        const bTime = getLatestRecordTimestamp(b, collectionName);
        if (aTime && bTime) return bTime - aTime;
        if (aTime) return -1;
        if (bTime) return 1;
        return 0;
      });

      const lastRecords = sortedDocs.slice(0, 10).map((doc: any) => {
        const dateValue = [
          "updatedAt",
          "createdAt",
          "addedAt",
          "date",
          "timestamp",
          "created_at",
          "updated_at",
          "added_at",
          "created",
          "updated",
          "registeredAt",
          "registered_at",
          "lastUpdated",
          "last_updated"
        ].map(fieldName => getFieldValue(doc, fieldName)).find(value => value !== undefined && value !== null && value !== "") ?? null;

        let dateStr = "brak daty";
        const dateMs = getTimestampValueMs(dateValue);
        if (dateMs > 0) {
          dateStr = new Date(dateMs).toLocaleString("pl-PL");
        }

        const record: any = { date: dateStr };

        if (collectionName.toLowerCase() === "users") {
          record.country = getFieldValue(doc, "country") || getFieldValue(doc, "kraj") || "";
          record.birthYear = getFieldValue(doc, "rokUrodzenia") || getFieldValue(doc, "rok") || getFieldValue(doc, "year") || getFieldValue(doc, "birthYear") || "";
          record.gender = getFieldValue(doc, "plec") || getFieldValue(doc, "gender") || getFieldValue(doc, "sex") || "";
          record.height = getFieldValue(doc, "height") || getFieldValue(doc, "wzrost") || "";
        } else {
          record.rok = getFieldValue(doc, "rok") || getFieldValue(doc, "year") || "";
          record.producent = getFieldValue(doc, "producent") || getFieldValue(doc, "producer") || "";
          record.model = getFieldValue(doc, "model") || "";
          record.rozmiar = getFieldValue(doc, "rozmiar") || getFieldValue(doc, "size") || "";
        }

        return record;
      });

      setStats({
        total: allDocs.length,
        lastRecords
      });
    } catch (error) {
      console.error("Błąd pobierania statystyk:", error);
    }
  };

  // Pobierz statystyki dla aktualnej kolekcji
  useEffect(() => {
    fetchStats(false);
  }, [collectionName]);

  const handleManualStatsRefresh = async () => {
    await fetchStats(true);
  };

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error("Błąd wylogowania:", error);
    }
  };

  return (
    <div style={{
      ...appStyles,
      padding: "10px",
      boxSizing: "border-box",
      width: "100%",
      maxWidth: "100%",
      overflow: "hidden"
    }}>
      {/* Górny wiersz: Tytuł, wybór kolekcji i Wyloguj */}
      <div style={{
        display: "flex",
        gap: "10px",
        width: "100%",
        alignItems: "center",
        marginBottom: "10px",
        justifyContent: "space-between"
      }}>
        <div style={{
          flex: "1"
        }}>
          <div style={{
            ...headerStyles,
            padding: "16px"
          }}>
            <h1 style={{ margin: 0, fontSize: "1.5rem" }}>FIZMOOBASE</h1>
          </div>
        </div>

        <div style={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
          justifyContent: "flex-end"
        }}>
          <div style={{
            ...headerStyles,
            padding: "10px 14px",
            display: "flex",
            alignItems: "center",
            gap: "10px",
            flexWrap: "wrap"
          }}>
            <div style={{ color: "#ccd6f6", fontSize: "11px", lineHeight: 1.5 }}>
              <div><span style={{ color: "#64ffda", fontWeight: "bold" }}>Producenci:</span> {collectionStats.producers}</div>
              <div><span style={{ color: "#64ffda", fontWeight: "bold" }}>Kraje:</span> {collectionStats.countries}</div>
            </div>
            <span style={{ color: "#64ffda", fontWeight: "bold", fontSize: "12px" }}>
              {stats.total} rekordów
            </span>
            <CollectionSelector
              onSelectCollection={setCollectionName}
              selectedCollection={collectionName}
              role={role}
              compact={true}
            />
          </div>

          <div style={{
            ...headerStyles,
            padding: "12px 16px",
            minWidth: "180px"
          }}>
            <button
              onClick={handleLogout}
              style={{
                ...logoutButtonStyles,
                width: "100%"
              }}
            >
              Wyloguj się
            </button>
          </div>
        </div>
      </div>

      {/* Statystyki bazy danych - pełna szerokość */}
      <div style={{
        ...headerStyles,
        padding: "16px",
        marginBottom: "10px",
        width: "100%",
        boxSizing: "border-box"
      }}>
        <div style={{
          backgroundColor: "#1e2d45",
          color: "#64ffda",
          borderRadius: "6px",
          padding: "12px",
          fontSize: "13px"
        }}>
          <div style={{ marginBottom: "8px" }}><strong>Statystyki kolekcji:</strong></div>
          <button
            onClick={handleManualStatsRefresh}
            style={{
              marginBottom: "10px",
              padding: "6px 12px",
              backgroundColor: "#64ffda",
              color: "#0a192f",
              border: "none",
              borderRadius: "4px",
              cursor: "pointer",
              fontWeight: "bold",
              fontSize: "12px"
            }}
          >
            Odśwież bazę/cache
          </button>
          <div>Rekordów: <b>{stats.total}</b></div>
          <div style={{ marginTop: "10px" }}>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", maxHeight: "300px", overflowY: "auto" }}>
              {stats.lastRecords.length === 0 ? (
                <div style={{ color: "#8892b0" }}>Brak danych</div>
              ) : (
                stats.lastRecords.map((rec, idx) => {
                  const genderValue = String(rec.gender || '').toLowerCase();
                  const isFemale = ['k', 'kobieta', 'female', 'f', 'woman'].includes(genderValue);
                  const hasHeight = rec.height !== undefined && rec.height !== null && String(rec.height).trim() !== '';
                  const hasBirthYear = rec.birthYear !== undefined && rec.birthYear !== null && String(rec.birthYear).trim() !== '';
                  const hasGender = rec.gender !== undefined && rec.gender !== null && String(rec.gender).trim() !== '';
                  const hasCountry = rec.country !== undefined && rec.country !== null && String(rec.country).trim() !== '';
                  return (
                    <div key={idx} style={{
                      backgroundColor: isFemale && collectionName.toLowerCase() === 'users' ? '#3b2a4f' : '#0a192f',
                      border: "1px solid #233554",
                      borderRadius: "4px",
                      padding: "8px 12px",
                      minWidth: "140px",
                      fontSize: "12px"
                    }}>
                      <div>
                        <span style={{ color: "#8892b0", fontSize: "10px" }}>
                          {rec.date}
                        </span>
                      </div>
                      <div style={{ marginTop: "6px", fontWeight: "bold", color: "#64ffda" }}>
                        {collectionName.toLowerCase() === 'users' ? (
                          // Format dla users: KRAJ-ROK URODZENIA-PŁEĆ-WZROST
                          <>
                            {hasCountry && <span>{rec.country}</span>}
                            {hasBirthYear && <span>-{rec.birthYear}</span>}
                            {hasGender && <span>-{rec.gender}</span>}
                            {hasHeight && <span>-{rec.height}</span>}
                          </>
                        ) : (
                          // Format dla bikeFrames: ROK_PRODUCENT_MODEL_ROZMIAR
                          <>
                            {rec.rok && <span>{rec.rok}</span>}
                            {rec.producent && <span> {rec.producent}</span>}
                            {rec.model && <span> {rec.model}</span>}
                            {rec.rozmiar && <span> {rec.rozmiar}</span>}
                          </>
                        )}
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Dolny wiersz: Tabela na pełnej szerokości */}
      <div style={{
        backgroundColor: "#112240",
        padding: "10px",
        borderRadius: "8px",
        boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        width: "100%",
        boxSizing: "border-box"
      }}>
        <DataTable collectionName={collectionName} onStatsChange={setCollectionStats} />
      </div>
    </div>
  );
};

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/producer-register" element={<ProducerRegister />} />
          <Route
            path="/admin"
            element={
              <ProtectedRoute role="admin">
                <MainPage />
              </ProtectedRoute>
            }
          />
          <Route
            path="/"
            element={<Navigate to="/admin" replace />}
          />
          <Route
            path="/my-bikes"
            element={
              <ProtectedRoute role="producer">
                <MyBikes />
              </ProtectedRoute>
            }
          />
          <Route
            path="/add-bike"
            element={
              <ProtectedRoute role="producer">
                <AddBikePage />
              </ProtectedRoute>
            }
          />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
